import { useMemo, useState } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { resolveRichText } from '@vibesites/runtime';
import {
  EditableCollectionBinding,
  RichTextContent,
  useCollectionBinding,
  useVibesites,
} from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

interface FAQ {
  question?: string | null;
  answer?: string | null;
  category?: string | null;
  [key: string]: unknown;
}

const UNCATEGORIZED = 'General';

function hasValue(val: unknown): val is string {
  if (val == null) return false;
  const s = String(val).trim();
  return s !== '' && s.toLowerCase() !== 'null' && s.toLowerCase() !== 'undefined';
}

/**
 * Group FAQ items by their `category` field. Items without a category fall
 * into a `General` bucket shown last, mirroring how the demo site clusters
 * uncategorized questions below the explicit groups.
 */
function groupByCategory(items: FAQ[]): Array<{ category: string; items: FAQ[] }> {
  const groups = new Map<string, FAQ[]>();

  for (const item of items) {
    const key = hasValue(item.category) ? item.category!.trim() : UNCATEGORIZED;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(item);
  }

  // Explicit categories first (insertion order), uncategorized last.
  const result: Array<{ category: string; items: FAQ[] }> = [];
  for (const [category, catItems] of groups.entries()) {
    if (category !== UNCATEGORIZED) result.push({ category, items: catItems });
  }
  const general = groups.get(UNCATEGORIZED);
  if (general) result.push({ category: UNCATEGORIZED, items: general });

  return result;
}

interface AccordionItemProps {
  faq: FAQ;
  index: number;
  isOpen: boolean;
  onToggle: () => void;
}

function AccordionItem({ faq, index, isOpen, onToggle }: AccordionItemProps) {
  const { property } = useVibesites();
  const answerHtml = useMemo(() => {
    if (!hasValue(faq.answer)) return '';
    return resolveRichText(faq.answer!, property as Record<string, any>);
  }, [faq.answer, property]);

  const panelId = `faq-panel-${index}`;
  const buttonId = `faq-button-${index}`;

  return (
    <div className={`faq-item${isOpen ? ' faq-item--open' : ''}`}>
      <button
        type="button"
        id={buttonId}
        className="faq-question"
        aria-expanded={isOpen}
        aria-controls={panelId}
        onClick={onToggle}
      >
        <span className="faq-question-text">{faq.question || 'Question'}</span>
        <span className="faq-icon" aria-hidden="true">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </span>
      </button>
      <div
        id={panelId}
        role="region"
        aria-labelledby={buttonId}
        className="faq-answer"
        hidden={!isOpen}
      >
        {answerHtml ? (
          <div
            className="faq-answer-body"
            dangerouslySetInnerHTML={{ __html: answerHtml }}
          />
        ) : (
          <p className="faq-answer-body faq-answer-body--empty">No answer provided.</p>
        )}
      </div>
    </div>
  );
}

export function FAQList() {
  const faqs = useCollectionBinding<FAQ>('faqlist.items', {
    collection: 'faqs',
  });

  const grouped = useMemo(() => groupByCategory(faqs || []), [faqs]);

  // Track open state per (groupIndex, itemIndex). An FAQ page typically
  // expects one answer at a time per category — clicking the same item
  // closes it; clicking another in the same category replaces the open one.
  const [openPerGroup, setOpenPerGroup] = useState<Record<number, number | null>>({});

  const toggle = (groupIdx: number, itemIdx: number) => {
    setOpenPerGroup((prev) => {
      const current = prev[groupIdx];
      return { ...prev, [groupIdx]: current === itemIdx ? null : itemIdx };
    });
  };

  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  const hasAnyFAQs = (faqs || []).length > 0;

  return (
    <section
      ref={sectionRef}
      className="reveal-fade-up pt-2 md:pt-4 pb-12 md:pb-20 bg-[var(--vibe-color-background)]"
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8 md:mb-12 text-center">
          <RichTextContent
            path="faqlist.intro"
            defaultValue=""
            as="div"
            className="faq-section-intro"
          />
        </div>

        <EditableCollectionBinding bindingPath="faqlist.items" label="FAQs">
          {hasAnyFAQs ? (
            <div className="faq-stack">
              {grouped.map((group, groupIdx) => (
                <div key={group.category} className="faq-group">
                  {grouped.length > 1 && (
                    <h3 className="faq-group-heading">{group.category}</h3>
                  )}
                  <div className="faq-list">
                    {group.items.map((faq, itemIdx) => {
                      // Use a stable key combining group + question text when
                      // present so React doesn't swap panel state across
                      // editor-driven reorders.
                      const key = `${group.category}-${itemIdx}-${faq.question || ''}`;
                      return (
                        <AccordionItem
                          key={key}
                          faq={faq}
                          index={groupIdx * 1000 + itemIdx}
                          isOpen={openPerGroup[groupIdx] === itemIdx}
                          onToggle={() => toggle(groupIdx, itemIdx)}
                        />
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-10 text-[var(--vibe-color-text-muted)]">
              <p className="font-body text-lg">
                FAQs will be displayed here once added to the property.
              </p>
            </div>
          )}
        </EditableCollectionBinding>
      </div>

      <style>{`
        .faq-section-intro {
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          line-height: 1.6;
          color: var(--vibe-color-text-muted);
          max-width: 42rem;
          margin: 0 auto;
        }
        .faq-section-intro p:empty { display: none; }

        .faq-stack {
          display: flex;
          flex-direction: column;
          gap: 2.5rem;
        }

        .faq-group-heading {
          font-family: "Josefin Slab", Rockwell, Georgia, serif;
          font-size: clamp(1.25rem, 2.5vw, 1.75rem);
          font-weight: 700;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: var(--vibe-color-text);
          margin: 0 0 1rem;
          padding-bottom: 0.5rem;
          border-bottom: 2px solid var(--vibe-color-primary);
        }

        .faq-list {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .faq-item {
          border: 1px solid color-mix(in srgb, var(--vibe-color-text) 14%, transparent);
          border-radius: 4px;
          background: #fff;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .faq-item--open {
          border-color: var(--vibe-color-primary);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
        }

        .faq-question {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          padding: 1rem 1.25rem;
          background: transparent;
          border: none;
          text-align: left;
          cursor: pointer;
          font-family: var(--vibe-font-heading);
          font-size: 0.95rem;
          font-weight: 600;
          letter-spacing: 0.03em;
          color: var(--vibe-color-text);
          transition: color 0.2s;
        }
        .faq-question:hover {
          color: var(--vibe-color-primary);
        }
        .faq-question:focus-visible {
          outline: 2px solid var(--vibe-color-primary);
          outline-offset: -2px;
        }

        .faq-question-text {
          flex: 1;
          line-height: 1.4;
        }

        .faq-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          width: 24px;
          height: 24px;
          color: var(--vibe-color-primary);
          transition: transform 0.3s ease;
        }
        .faq-item--open .faq-icon {
          transform: rotate(180deg);
        }

        .faq-answer {
          padding: 0 1.25rem 1.25rem;
        }

        .faq-answer-body {
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          line-height: 1.65;
          color: var(--vibe-color-text);
        }
        .faq-answer-body p { margin: 0 0 0.75rem; }
        .faq-answer-body p:last-child { margin-bottom: 0; }
        .faq-answer-body ul,
        .faq-answer-body ol {
          margin: 0 0 0.75rem;
          padding-left: 1.5rem;
        }
        .faq-answer-body ul { list-style: disc; }
        .faq-answer-body ol { list-style: decimal; }
        .faq-answer-body li { margin-bottom: 0.25rem; }
        .faq-answer-body a {
          color: var(--vibe-color-primary);
          text-decoration: underline;
        }
        .faq-answer-body a:hover {
          opacity: 0.8;
        }
        .faq-answer-body--empty {
          color: var(--vibe-color-text-muted);
          font-style: italic;
        }

        @media (prefers-reduced-motion: reduce) {
          .faq-icon,
          .faq-item { transition: none; }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FAQList',
  displayName: 'FAQ List',
  tokens: {
    'faqlist.intro': {
      label: 'Section Intro',
      type: 'richtext',
      description:
        'Short lead paragraph shown above the FAQ list. Leave blank to omit.',
      defaultValue: '',
    },
  },
  collectionBindings: {
    'faqlist.items': {
      label: 'FAQs',
      collection: 'faqs',
      description:
        'FAQ items from the property. If items have a `category` set, they are grouped by category on the page; items without a category appear under a "General" group.',
    },
  },
};

(FAQList as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default FAQList;
